import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius, fonts } from '../theme';
import { findProductById, PRODUCTS } from '../data/products';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M05ProductDetailScreen'>;
type RouteProps = RouteProp<RootStackParamList, 'M05ProductDetailScreen'>;

export const M05ProductDetailScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  
  // Default to Xanh Ước Mơ ('XUM') if no productId is passed
  const productId = route.params?.productId || 'XUM';
  const product = findProductById(productId) || PRODUCTS[0];

  const handleCreateIllustration = () => {
    navigation.navigate('M06InputCustomerScreen', { productId: product.id });
  };

  const getProductDesc = (pId: string) => {
    if (pId === 'XUM') {
      return 'Xanh Ước Mơ là giải pháp bảo hiểm liên kết chung ưu việt, được thiết kế đặc biệt nhằm giúp các cha mẹ chuẩn bị quỹ tài chính vững chắc cho tương lai học vấn của con trẻ, kết hợp bảo vệ gia đình toàn diện trước mọi biến cố rủi ro cuộc sống. Sản phẩm cam kết tích lũy tài sản an toàn với lãi suất đầu tư ổn định và gia tăng bền vững.';
    }
    if (pId === 'XTL' || pId === 'XTL-PS') {
      return 'Xanh Tương Lai là giải pháp liên kết đơn vị mang lại cơ hội gia tăng tài sản mạnh mẽ thông qua việc đầu tư linh hoạt vào các quỹ cổ phiếu và trái phiếu do chuyên gia Manulife quản lý, đồng thời bảo vệ tài chính vững vàng cho gia đình.';
    }
    if (pId === 'XPQ') {
      return 'Xanh Phú Quý là giải pháp bảo hiểm liên kết đầu tư cao cấp giúp bảo vệ gia đình và đầu tư tích lũy thông minh cho tương lai thịnh vượng của dòng họ, kiến tạo một gia sản vững bền cho thế hệ tương lai.';
    }
    return 'Giải pháp bảo hiểm số cao cấp giúp bảo vệ gia đình và đầu tư tích lũy thông minh cho tương lai thịnh vượng.';
  };

  const getTargetAudience = (pId: string) => {
    if (pId === 'XUM') {
      return [
        'Các cha mẹ có con nhỏ mong muốn chuẩn bị quỹ học vấn tương lai vững vàng.',
        'Khách hàng muốn có giải pháp bảo vệ kết hợp tích lũy an toàn, cam kết và sinh lời hiệu quả.',
        'Gia đình cần bảo vệ tài chính trước các biến cố rủi ro cuộc sống.',
      ];
    }
    if (pId === 'XPQ') {
      return [
        'Khách hàng có thu nhập cao mong muốn thiết lập di sản thừa kế cho con cháu.',
        'Khách hàng có khẩu vị rủi ro cao, muốn đầu tư sinh lời vượt trội trên thị trường tài chính.',
      ];
    }
    return [
      'Khách hàng mong muốn kết hợp bảo vệ sức khỏe toàn diện và đầu tư tích lũy linh hoạt.',
      'Người muốn gia tăng tài sản thông qua các quỹ đầu tư chuyên nghiệp do Manulife quản lý.',
      'Gia đình trẻ cần giải pháp bảo vệ tài chính trọn đời.',
    ];
  };

  const benefits = product.features.length > 0 ? product.features : [
    'Bảo vệ trọn đời trước các rủi ro trong cuộc sống',
    'Tích lũy hiệu quả với lãi suất cam kết và lãi suất gia tăng',
    'Thưởng duy trì hợp đồng định kỳ hấp dẫn',
    'Gia tăng số tiền bảo hiểm khi có thành viên mới trong gia đình'
  ];

  const targetAudience = getTargetAudience(product.id);

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        {/* Banner Area */}
        <View style={styles.banner}>
          <Icon name="shield" size={48} color="#ffffff" />
          <Text style={styles.bannerTitle}>{product.name}</Text>
          <Text style={styles.bannerSub}>{product.tagline}</Text>
        </View>

        {/* Product Details Content */}
        <View style={styles.content}>
          <View style={styles.infoCard}>
            <Text style={styles.infoCardTitle}>📋 Thông tin chính</Text>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Loại sản phẩm:</Text>
              <Text style={styles.infoValue}>Bảo hiểm {product.typeLabel.toLowerCase()}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Độ tuổi bảo vệ:</Text>
              <Text style={styles.infoValue}>30 ngày tuổi - 99 tuổi</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Thời hạn đóng phí:</Text>
              <Text style={styles.infoValue}>10 / 15 / 20 năm hoặc trọn đời</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Phí đóng ước tính:</Text>
              <Text style={styles.infoValue}>Từ 10.000.000 VNĐ / năm</Text>
            </View>
          </View>

          {/* Quyền lợi nổi bật */}
          <Text style={styles.sectionTitle}>🎯 Quyền lợi nổi bật</Text>
          <View style={styles.benefitList}>
            {benefits.map((feat, i) => (
              <View key={i} style={styles.benefitItem}>
                <Icon name="check_circle" size={20} color={colors.secondary} />
                <Text style={styles.benefitText}>{feat}</Text>
              </View>
            ))}
          </View>

          {/* Mô tả chi tiết */}
          <Text style={styles.sectionTitle}>📖 Mô tả sản phẩm</Text>
          <Text style={styles.descText}>
            {getProductDesc(product.id)}
          </Text>

          {/* Phù hợp với ai */}
          <Text style={styles.sectionTitle}>💡 Đối tượng phù hợp</Text>
          <View style={styles.fitList}>
            {targetAudience.map((target, i) => (
              <Text key={i} style={styles.fitText}>• {target}</Text>
            ))}
          </View>
        </View>
      </ScrollView>

      {/* CTA Button at Bottom */}
      <View style={styles.bottomBar}>
        <TouchableOpacity style={styles.ctaBtn} onPress={handleCreateIllustration}>
          <Text style={styles.ctaBtnText}>TẠO MINH HỌA NGAY</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContainer: {
    paddingBottom: 88, // Space for sticky bottom bar
  },
  banner: {
    backgroundColor: colors.primary,
    paddingVertical: spacing[8],
    paddingHorizontal: spacing[4],
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerTitle: {
    fontFamily: fonts.headline,
    fontSize: 24,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
    marginTop: spacing[2],
  },
  bannerSub: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    marginTop: spacing[1],
    textAlign: 'center',
  },
  content: {
    padding: spacing[4],
  },
  infoCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    marginBottom: spacing[5],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  infoCardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing[2],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  infoLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  infoValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginTop: spacing[2],
    marginBottom: spacing[3],
  },
  benefitList: {
    gap: spacing[2.5],
    marginBottom: spacing[5],
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2.5],
  },
  benefitText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: colors.onSurface,
  },
  descText: {
    fontSize: 13,
    lineHeight: 20,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[5],
    textAlign: 'justify',
  },
  fitList: {
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  fitText: {
    fontSize: 13,
    lineHeight: 18,
    color: colors.onSurface,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    padding: spacing[4],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
  },
  ctaBtn: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.5,
  },
});
